import styled from "@emotion/styled";
import { ButtonClickAnalyticsType, FieldTypeOption } from "@kaltura/mediaspace-shared-types";
import EventRecording from "./event-recording/EventRecording";
import { NameField } from "@kaltura/mediaspace-shared-ui";
import { Schedule } from "@kaltura/mediaspace-shared-ui/form";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { baseUrl, postKmsData, translate, useGuessedTimeZone } from "@kaltura/mediaspace-shared-utils";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { useEffect } from "react";
import { TemplateSelection, Template } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { CustomIllustration, illustrationsMap } from "./illustrations";
import { RecordStatusValue } from "../types";
import { DVR } from "./dvr/dvr";

export interface CreateTownhallFormProps {
    /**
     * list of available timezones
     */
    timezones: FieldTypeOption[];

    /**
     * list of available recording options
     */
    recordingOptions: Record<RecordStatusValue, string>;

    /**
     * default recording mode to use in the form
     */
    defaultRecordingMode: string;

    /**
     * Path part of the URL to which the form data will be submitted (will be appended to baseUrl)
     */
    submissionUrl: string;

    /**
     * Function to handle form submission
     * @param data - form data
     */
    onSubmit: (data: { [key: string]: string | boolean }) => Promise<void>;

    /**
     * Form ID for accessibility purposes
     */
    formId: string;

    /**
     * Function to update parent's processing state
     */
    updateProcessing: (value: boolean) => void;

    /**
     * Function to update parent's disable button state
     */
    updateDisableButton: (value: boolean) => void;

    /**
     * This is KMS' global DVR config. When enabled - a kWebcast creator can decide whether to enable DVR individually, per entry.
     */
    dvrEnabled: boolean;

    /**
     * function to get list of templates to display in the form
     */
    fetchTemplates?: () => Template[];
}

const StyledForm = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

const baseClassName = "kms-ds-create-townhall-form";

/**
 * Create Townhall Form component
 */
const CreateTownhallForm = (props: CreateTownhallFormProps) => {
    const {
        timezones,
        recordingOptions,
        defaultRecordingMode,
        dvrEnabled,
        formId,
        updateProcessing,
        updateDisableButton,
        submissionUrl,
        fetchTemplates,
    } = props;

    const sendButtonAnalytics = useButtonAnalytics();

    const { showToast } = useToastsContext();

    const guessedTz = useGuessedTimeZone();

    const templates = fetchTemplates?.();

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            tz: guessedTz,
            ...(templates && templates.length ? { roomMode: templates[0].id } : {}),
        },
    });

    // Update parent component when form validation state changes
    useEffect(() => {
        updateDisableButton(!useFormHookMethods.formState.isValid);
    }, [useFormHookMethods.formState.isValid, updateDisableButton]);

    const onSubmit = async (data: FieldValues) => {
        updateProcessing(true);
        if (fetchTemplates) {
            // creating a live-studio with a room template
            sendButtonAnalytics("Add - Live-studio", ButtonClickAnalyticsType.CREATE);
        }
        else {
            // creating a townhall
            sendButtonAnalytics("Add - Webcast", ButtonClickAnalyticsType.CREATE);
        }

        try {
            const result = await postKmsData(`${baseUrl}${submissionUrl}`, data);
            if (result.success && result.redirectUrl) {
                // redirect to the new townhall page on success
                window.location.href = result.redirectUrl;
            }
            if (!result.success) {
                // show toast in case of other errors
                showToast({ severity: "error", message: result.errorMessage });
                updateProcessing(false);
            }
        }
        catch (e) {
            updateProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create town hall") });
        }
    };

    return (
        <FormProvider {...useFormHookMethods}>
            <StyledForm className={baseClassName} id={formId} onSubmit={useFormHookMethods.handleSubmit(onSubmit)}>
                <NameField id={"name"} label={translate("Name")} className={`${baseClassName}-name`} />
                {templates && templates.length > 0 && (
                    <ButtonAnalyticsProvider namePrefix={"Toggle Live studio template -"}>
                        <TemplateSelection
                            labelSingle={translate("Session type")}
                            labelMultiple={translate("Choose type")}
                            templates={templates}
                            illustrationsMap={illustrationsMap}
                            defaultIllustration={CustomIllustration}
                        />
                    </ButtonAnalyticsProvider>
                )}
                <Schedule timezones={timezones} />
                <EventRecording recordingOptions={recordingOptions} defaultRecordingMode={defaultRecordingMode} />
                {dvrEnabled ? <DVR /> : null}
                <input type="submit" hidden aria-hidden="true" />
            </StyledForm>
        </FormProvider>
    );
};

export default CreateTownhallForm;
